/**
 * Drops locations the Location Service returned without a usable `distance`,
 * applying no radius filter at all.
 *
 * Backs the nearest-locations list, which is deliberately range-agnostic so
 * consumers can still show the closest stores when a radius search comes back
 * empty. Order is preserved, so the caller inherits the service's
 * distance-ascending ordering.
 *
 * @param {Array<Object>} locations - Locations from the Location Service, each with a `distance` in miles.
 * @returns {Array<Object>} The locations with a numeric `distance`, in the order received; empty array if `locations` is not an array.
 * @example
 * getLocationsWithDistance([{ distance: 10 }, { distance: 500 }, {}]);
 * // Returns [{ distance: 10 }, { distance: 500 }]
 */
export const getLocationsWithDistance = (locations) => {
	if (!Array.isArray(locations)) return [];

	return locations.filter(
		(location) => !Number.isNaN(parseFloat(location?.distance))
	);
};

/**
 * Filters locations down to those within a given radius of the searched postal code.
 * Locations without a numeric `distance` are dropped, as is everything when `range`
 * itself is not numeric.
 *
 * @param {Array<Object>} locations - Locations from the Location Service, each with a `distance` in miles.
 * @param {number|string} range - Maximum distance (miles) a location may be from the search origin.
 * @returns {Array<Object>} The locations within `range`; empty array if `locations` is not an array.
 * @example
 * getFilteredLocationsByRange([{ distance: 10 }, { distance: 75 }], 50);
 * // Returns [{ distance: 10 }]
 * getFilteredLocationsByRange([{ distance: 10 }, {}], '50');
 * // Returns [{ distance: 10 }]
 */
export const getFilteredLocationsByRange = (locations, range) => {
	const maxDistance = parseFloat(range);

	return getLocationsWithDistance(locations).filter(
		(location) => parseFloat(location.distance) <= maxDistance
	);
};
